import { CWILL_EVENT_NAMES } from "@channelwill/report"
import { Card, Page, SkeletonBodyText } from "@shopify/polaris"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import Slider from "react-slick"

import commonAPI from "@/api/common"
import { RecommendApp, RecommendAppsBanner } from "@/api/common/types/RecommendApps"
import { Container } from "@/component"
import APPCard from "@/pages/Dashboard/components/RecommendedCard/APPCard"
import MonorailTracker from "@/utils/monorailTracker"

import styles from "./Recommend.module.scss"

const RecommendPage: React.FC = () => {
  const { t } = useTranslation("dashboard")
  const navigate = useNavigate()
  const [recommendedApps, setRecommendedApps] = useState<RecommendApp[]>([])
  const [banners, setBanners] = useState<RecommendAppsBanner[]>([])
  const [loading, setLoading] = useState(true)
  // 跟踪已经查看过的 banner，避免重复上报
  const [viewedBanners, setViewedBanners] = useState<Set<number>>(new Set())

  const handleBackClick = () => {
    navigate({ pathname: "/" })
  }

  // 处理轮播图切换时的 view 事件
  const handleSlideChange = (currentSlide: number) => {
    if (banners.length > 0 && currentSlide < banners.length) {
      const banner = banners[currentSlide]

      if (banner.id && !viewedBanners.has(banner.id)) {
        MonorailTracker.trackRecommendAppViewed(banner.id)
        window.cwreport && window.cwreport.report(CWILL_EVENT_NAMES.RECOMMEND_APP_EXPOSURE, {
          position: "recommended_apps_banner",
          /** 推荐app id */
          recommend_app_id: banner.id ? String(banner.id) : "",
          /** 推荐app名称 */
          recommend_app_name: banner.name,
        })
        setViewedBanners(prev => new Set(prev).add(banner.id))
      }
    }
  }

  // 获取推荐应用数据
  useEffect(() => {
    const fetchRecommendApps = async () => {
      try {
        setLoading(true)
        const response = await commonAPI.getRecommendApps()

        if (response.data?.recommend_apps) {
          setRecommendedApps(response.data.recommend_apps.apps || [])
          setBanners(response.data.recommend_apps.banners || [])
          MonorailTracker.trackRecommendAppPageViewed()
        }
      } catch (error) {
        console.error("Failed to fetch recommend apps:", error)
        // 如果API失败，使用默认数据
        setRecommendedApps([])
        setBanners([])
      } finally {
        setLoading(false)
      }
    }

    fetchRecommendApps()
  }, [])

  // 当 banners 数据加载完成后，立即上报第一个 banner 的 view 事件
  useEffect(() => {
    if (banners.length > 0 && !loading) {
      const firstBanner = banners[0]

      if (firstBanner.id && !viewedBanners.has(firstBanner.id)) {
        setTimeout(() => {
          MonorailTracker.trackRecommendAppViewed(firstBanner.id)
          window.cwreport && window.cwreport.report(CWILL_EVENT_NAMES.RECOMMEND_APP_EXPOSURE, {
            position: "recommended_apps_banner",
            /** 推荐app id */
            recommend_app_id: firstBanner.id ? String(firstBanner.id) : "",
            /** 推荐app名称 */
            recommend_app_name: firstBanner.name,
          })
          setViewedBanners(prev => new Set(prev).add(firstBanner.id))
        }, 600)
      }
    }
  }, [banners, loading, viewedBanners])

  // 轮播图设置 - 根据数据量动态控制指示器显示
  const carouselSettings = {
    dots: banners.length > 1, // 只有多个项目时才显示指示器
    infinite: banners.length > 1, // 只有多个项目时才无限循环
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: banners.length > 1, // 只有多个项目时才自动播放
    speed: 500,
    arrows: false,
    autoplaySpeed: 3000,
    cssEase: "ease-in-out",
    // 移动端触摸优化
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    touchThreshold: 5,
    // 移动端自动播放暂停设置
    pauseOnHover: true,
    pauseOnFocus: true,
    pauseOnDotsHover: true,
    // 轮播图切换事件
    afterChange: handleSlideChange,
    // 响应式设置
    responsive: [
      {
        breakpoint: 768,
        settings: {
          autoplaySpeed: 4000, // 移动端稍微慢一点
          pauseOnHover: false, // 移动端不支持hover
        },
      },
    ],
  }

  return (
    <Container maxWidth={"950px"} className={`mt-0 ${styles.mobileContainer}`} fullWidth={false}>
      <Page
        title={t("MoreApps")}
        backAction={{
          content: "More top apps",
          onAction: handleBackClick,
        }}
      >
        {/* 轮播图部分 */}
        {loading ? (
          <div className={styles.SliderWrapper}>
            <div
              className={styles.sliderItem}
              style={{
                height: "317px",
                backgroundColor: "#f6f6f7",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              {/* 主要骨架屏背景 */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(90deg, #f6f6f7 25%, #e8e8e8 50%, #f6f6f7 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 2s infinite",
                }}
              />
            </div>
          </div>
        ) : banners.length > 0 ? (
          <div
            className={styles.SliderWrapper}
            style={{ paddingBottom: banners.length > 1 ? undefined : 0 }}
          >
            <Slider {...carouselSettings}>
              {banners.map((banner, index) => (
                <div key={index}>
                  <div
                    className={styles.sliderItem}
                    style={{
                      height: "317px",
                      backgroundImage: banner.img_url ? `url(${banner.img_url})` : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      window.open(banner.url, "_blank")
                      MonorailTracker.trackRecommendAppClicked(banner.id)

                      window.cwreport && window.cwreport.report(CWILL_EVENT_NAMES.RECOMMEND_APP_CLICK, {
                        position: "recommended_apps_banner",
                        recommend_app_id: banner.id ? String(banner.id) : "",
                        recommend_app_name: banner.name,
                      })
                    }}
                  />
                </div>
              ))}
            </Slider>
          </div>
        ) : null}

        {/* 推荐应用列表 */}
        {loading ? (
          <div className={styles.appsGrid}>
            {/* 骨架屏 */}
            {Array.from({ length: 6 }).map((_, index) => (
              <Card key={index} padding={"400"}>
                <SkeletonBodyText lines={5} />
              </Card>
            ))}
          </div>
        ) : (
          <div className={styles.appsGrid}>
            {recommendedApps.length > 0 ? (
              recommendedApps.map((app, index) => (
                <div
                  key={app.id}
                  data-cwill-report-name="recommend_app_exposure"
                  // 曝光事件属性
                  data-cwill-report-attr={JSON.stringify({
                    position: "recommended_apps_list",
                    recommend_app_id: app.id ? String(app.id) : "",
                    recommend_app_name: app.name,
                  })}
                >
                  <APPCard
                    id={app.id}
                    padding={"400"}
                    logo={app.img_url}
                    title={app.name}
                    description={app.description}
                    link={app.url}
                    badges={app.badges}
                    position="recommended_apps_list"
                  />
                </div>
              ))
            ) : (
              /* 如果没有数据，显示占位内容 */
              <>
                <APPCard
                  padding={"400"}
                  logo="https://cdn.shopify.com/app-store/listing_images/f85ee597169457da8ee70b6652cae768/icon/CIuQqLP0lu8CEAE=.jpg"
                  title="PageFly Landing Page Builder"
                  description="From ineffective to outperformed storefront. Fast. Effortless."
                  link={"#"}
                  badges={[]}
                />
                <APPCard
                  padding={"400"}
                  logo="https://cdn.shopify.com/app-store/listing_images/f85ee597169457da8ee70b6652cae768/icon/CIuQqLP0lu8CEAE=.jpg"
                  title="PageFly Landing Page Builder"
                  description="From ineffective to outperformed storefront. Fast. Effortless."
                  link={"#"}
                  badges={[]}
                />
                <APPCard
                  padding={"400"}
                  logo="https://cdn.shopify.com/app-store/listing_images/f85ee597169457da8ee70b6652cae768/icon/CIuQqLP0lu8CEAE=.jpg"
                  title="PageFly Landing Page Builder"
                  description="From ineffective to outperformed storefront. Fast. Effortless."
                  link={"#"}
                  badges={[]}
                />
              </>
            )}
          </div>
        )}
      </Page>
    </Container>
  )
}

export default RecommendPage
